import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Checkbox,
    FormControlLabel,
    TextField,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add as AddIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Folder as FolderIcon, Description as DescriptionIcon } from '@mui/icons-material';
import { useDocument } from '../../contexts/DocumentContext';
import { useHelpContent } from '../../utils/HelpUtils';
import { Document } from '../../types/Document';
import { Category } from '../../types/Category';
import api from '../../services/api';
import {
  fetchCategoryWithChildren,
  updateCategoryInTree,
} from '../../services/CategoryService';
import {
  findCategoryInTree,
  normalizeCategoryName,
} from '../../utils/CategoryUtils';
import { Content } from '../../types/Content';

function countContentNodes(node: any): number {
    if (!node) return 0;
    let count = node.type === 'content' ? 1 : 0;
    if (Array.isArray(node.children)) {
        for (const child of node.children) count += countContentNodes(child);
    }
    return count;
}

function isContentCategoryNode(category: any): boolean {
    return category?.contentCategory === true || category?.isContentCategory === true;
}

interface ContentCategoryOption {
    id: string;
    label: string;
}

function collectContentCategoryOptions(
    category: any,
    path: string[] = [],
    isRoot = false,
): ContentCategoryOption[] {
    if (!category) return [];
    const currentPath = isRoot ? path : [...path, category.name?.trim() || 'Category'];
    const options: ContentCategoryOption[] = [];
    if (isContentCategoryNode(category)) {
        const labelPath = isRoot ? [category.name?.trim() || 'Category'] : currentPath;
        options.push({ id: category.id, label: labelPath.join(' / ') });
    }
    for (const child of category.children ?? []) {
        options.push(...collectContentCategoryOptions(child, currentPath, false));
    }
    return options;
}

interface ExpandTreeResultsDialogProps {
    open: boolean;
    onClose: () => void;
    expansionResult: any;
    onApplyExpansion: () => Promise<void>;
    currentDocument?: Document;
    existingDocuments?: any[];
    onOpenDocument?: (
        document: any,
        options?: { forceExpandRoot?: boolean },
    ) => Promise<void>;
    refreshCurrentDocument?: () => Promise<void>;
    refreshDocuments?: () => Promise<void>;
    onNewDocument?: (name: string, open: boolean) => Promise<any>;
    setGlobalLoading?: (loading: boolean) => void;
}

const ExpandTreeResultsDialog: React.FC<ExpandTreeResultsDialogProps> = ({
    open,
    onClose,
    expansionResult,
    onApplyExpansion,
    currentDocument,
    existingDocuments,
    onOpenDocument,
    refreshCurrentDocument,
    refreshDocuments,
    onNewDocument,
    setGlobalLoading,
}) => {
    const [applying, setApplying] = useState(false);
    const [showContent, setShowContent] = useState(true);
    const [subcaseName, setSubcaseName] = useState('Expanded');
    const [mergingOccurred, setMergingOccurred] = useState(false);
    const [processedTree, setProcessedTree] = useState<any>(null);
    const [existingCategories, setExistingCategories] = useState<Category[]>([]);
    const [fetchingCategories, setFetchingCategories] = useState(false);
    const [createNewDocument, setCreateNewDocument] = useState(false);
    const [newDocumentName, setNewDocumentName] = useState('');
    const [expansionMode, setExpansionMode] = useState<'with-content' | 'content-in-category' | 'categories-only'>('with-content');
    const [showFullDocumentTree, setShowFullDocumentTree] = useState(false);
    // For 'content-in-category' mode: '' = create a new "Content of [Document Name]" category,
    // otherwise the id of an existing content category to collect content into.
    const [contentInCategoryTargetId, setContentInCategoryTargetId] = useState<string>('');

    // Generate default name for new document
    const generateDefaultName = (baseName: string): string => {
        if (!baseName) return 'New Document';

        // Check if the name already ends with "Copy" or "Copy(n)"
        const copyPattern = / Copy(?: \((\d+)\))?$/;
        const match = baseName.match(copyPattern);

        let baseWithoutCopy: string;
        let startIndex: number;

        if (match) {
            // Name already ends with "Copy" or "Copy(n)" - extract base and increment
            baseWithoutCopy = baseName.replace(copyPattern, '');
            startIndex = match[1] ? parseInt(match[1]) + 1 : 1;
        } else {
            // Name doesn't end with "Copy" - add "Copy (1)"
            baseWithoutCopy = baseName;
            startIndex = 1;
        }

        // Find the next available index by checking existing documents
        if (existingDocuments && existingDocuments.length > 0) {
            let index = startIndex;
            let suggestedName = `${baseWithoutCopy} Copy (${index})`;

            while (existingDocuments.some(doc => doc.name === suggestedName)) {
                index++;
                suggestedName = `${baseWithoutCopy} Copy (${index})`;
            }

            return suggestedName;
        } else {
            // No existing documents to check - return the first suggestion
            return `${baseWithoutCopy} Copy (${startIndex})`;
        }
    };

    // Handle checkbox change
    const handleCreateNewDocumentChange = (checked: boolean) => {
        setCreateNewDocument(checked);
        if (checked && currentDocument) {
            // Generate default name when checkbox is checked
            const baseName = currentDocument.name || 'Document';
            const defaultName = generateDefaultName(baseName);
            setNewDocumentName(defaultName);
        } else {
            // Clear name when checkbox is unchecked
            setNewDocumentName('');
        }
    };
    const theme = useTheme();
    const { getTooltipTextForState } = useHelpContent();
    const { refreshDocumentSubcases } = useDocument();
    const isContentOnly = expansionResult?.contentOnly === true;

    /** Preview-only bucket from validate_and_fix_tree_structure — not applied to DB. */
    const PREVIEW_UNCATEGORIZED_CATEGORY = 'Uncategorized Content';

    /** When adding a subcategory under a content category, existing rows must move first (createCategory flips the parent). */
    const contentBucketByParentIdRef = useRef(new Map<string, string>());
    /** Fresh tree loaded at apply time (state alone can be stale when Apply is clicked). */
    const categorySnapshotRef = useRef<Category[]>([]);

    const appendContentToSnapshot = (parentId: string, created: Content) => {
        categorySnapshotRef.current = categorySnapshotRef.current.map(root =>
            updateCategoryInTree([root], parentId, cat => ({
                ...cat,
                contents: [...(cat.contents || []), created],
            }))[0],
        );
    };

    /** Create content without duplicate scan (bulk apply from files). */
    const postContentIfAbsent = async (
        parentId: string,
        subcase: string,
        text: string,
        richTextHtml?: string,
        richTextDelta?: string,
    ): Promise<Content | null> => {
        const response = await api.post('/content', {
            text,
            subcase,
            parentId,
            skipDuplicateCheck: true,
            ...(richTextHtml ? { richTextHtml } : {}),
            ...(richTextDelta ? { richTextDelta } : {}),
        });
        const created = response.data as Content;
        if (!createNewDocument) {
            appendContentToSnapshot(parentId, created);
        }
        return created;
    };

    const categoryIsContentCategory = (category: { contentCategory?: boolean; isContentCategory?: boolean }): boolean =>
        category.contentCategory === true || category.isContentCategory === true;

    const hasNewDescendants = (node: any): boolean => {
        if (!node) return false;
        if (node.new === true) return true;
        if (node.type === 'content' && node.new === true) return true;
        if (Array.isArray(node.children)) {
            return node.children.some((child: any) => hasNewDescendants(child));
        }
        return false;
    };

    const hasNewContentToApply = (node: any): boolean =>
        Array.isArray(node?.children) &&
        node.children.some((child: any) => child?.type === 'content' && child.new === true);

    // Apply merging when expansionResult changes
    useEffect(() => {
        if (expansionResult && expansionResult.tree) {
            const mergeResult = mergeDuplicateCategories(expansionResult.tree);
            const validatedTree = validateAndFixTreeStructure(mergeResult.node);
            setMergingOccurred(mergeResult.merged);
            setProcessedTree(validatedTree);
        }
    }, [expansionResult]);

    // Fetch categories when dialog opens
    useEffect(() => {
        if (open && currentDocument) {
            fetchCategoriesForDocument();
        }
    }, [open, currentDocument]);

    // Existing content categories of the current document (targets for "content in category").
    const contentCategoryOptions = useMemo<ContentCategoryOption[]>(() => {
        const root = existingCategories[0];
        if (!root) return [];
        return collectContentCategoryOptions(root, [], true);
    }, [existingCategories]);

    // Reset the chosen target if it is no longer a valid option or mode/new-doc changes.
    useEffect(() => {
        if (contentInCategoryTargetId &&
            !contentCategoryOptions.some((o) => o.id === contentInCategoryTargetId)) {
            setContentInCategoryTargetId('');
        }
    }, [contentCategoryOptions, contentInCategoryTargetId]);

    useEffect(() => {
        if (expansionMode !== 'content-in-category' || createNewDocument) {
            setContentInCategoryTargetId('');
        }
    }, [expansionMode, createNewDocument]);

    const fetchCategoriesForDocument = async () => {
        if (!currentDocument) return;

        setFetchingCategories(true);
        try {
            const document = currentDocument;

            if (document && document.categoryId) {
                try {
                    await api.post(
                        `/categories/repair-orphaned-content?categoryId=${encodeURIComponent(document.categoryId)}`
                    );
                } catch (repairError) {
                    console.error('Failed to repair orphaned content before expand preview', repairError);
                }
                const rootCategory = await fetchCategoryWithChildren(document.categoryId);
                categorySnapshotRef.current = [rootCategory];
                setExistingCategories([rootCategory]);
            } else {
                categorySnapshotRef.current = [];
                setExistingCategories([]);
            }
        } catch (error: any) {
            console.error('Error fetching categories for document:', error);
            setExistingCategories([]);
        } finally {
            setFetchingCategories(false);
        }
    };

    // Helper function to detect content categories
    const isContentCategory = (node: any): boolean => {
        if (!node.children || node.children.length === 0) {
            return true; // No children = content category (empty category)
        }
        // Has children of type 'content' = content category
        return node.children.some((child: any) => child.type === 'content');
    };

    // Helper function to check if a category is new (using the 'new' flag from the embedding service)
    const isNewCategory = (node: any): boolean => {
        if (!node || !node.name) return false;

        // Check if the embedding service has marked this category as new
        if (node.new === true) {
            return true;
        }

        // If no existing categories are loaded yet, we can't determine if it's new
        if (!existingCategories || existingCategories.length === 0) {
            return false;
        }

        // Check if it exists in the current structure
        const searchInExisting = (categories: Category[], targetName: string): boolean => {
            const targetNn = normalizeCategoryName(targetName);
            for (const category of categories) {
                if (normalizeCategoryName(category.name) === targetNn) {
                    return true;
                }
                if (category.children && category.children.length > 0) {
                    if (searchInExisting(category.children, targetName)) return true;
                }
            }
            return false;
        };

        return !searchInExisting(existingCategories, node.name);
    };

    // Simple approach: Only mark categories as new if they have the explicit 'new: true' flag
    const isNewCategorySimple = (node: any): boolean => {
        if (!node || !node.name) return false;

        // Only trust the explicit 'new: true' flag from the backend
        // Don't try to guess or infer
        return node.new === true;
    };

    // Helper to detect RTL (Hebrew/Arabic)
    const isRTL = (node: any): boolean => {
        if (!node) return false;
        if (node.name && /[\u0590-\u05FF\u0600-\u06FF]/.test(node.name)) return true;
        if (node.text && /[\u0590-\u05FF\u0600-\u06FF]/.test(node.text)) return true;
        return false;
    };

    // Helper function to validate and fix tree structure
    const validateAndFixTreeStructure = (node: any): any => {
        if (!node) return node;

        // If this is a content node, it should always be wrapped in a category.
        // This handles cases where the root itself is a content node, or a content node is directly under a category.
        if (node.type === 'content') {
            return node;
        }

        // If this is a category, validate its children
        if (node.type === 'category' && Array.isArray(node.children)) {
            // Recursively validate and fix children first
            const processedChildren = node.children.map((child: any) => validateAndFixTreeStructure(child));

            // Separate children into actual categories and categories that were created to hold content
            const actualCategoryChildren = processedChildren.filter((child: any) => child.type === 'category' && child.children.some((grandchild: any) => grandchild.type === 'category'));
            const contentHoldingCategories = processedChildren.filter((child: any) => child.type === 'category' && child.children.every((grandchild: any) => grandchild.type === 'content'));

            // If there are actual category children AND content-holding categories at this level,
            // consolidate the content-holding categories under a single new category.
            if (actualCategoryChildren.length > 0 && contentHoldingCategories.length > 0) {
                const allContentFromHoldingCategories: any[] = [];
                contentHoldingCategories.forEach((cat: any) => {
                    if (cat.children) {
                        allContentFromHoldingCategories.push(...cat.children);
                    }
                });

                const anyNewContent = allContentFromHoldingCategories.some(
                    (c: any) => c?.new === true
                );
                const anyNewHoldingCat = contentHoldingCategories.some(
                    (c: any) => c?.new === true
                );
                const consolidatedContentCategory = {
                    type: 'category',
                    name: generateCategoryNameFromContent(allContentFromHoldingCategories), // Use helper for naming
                    children: allContentFromHoldingCategories,
                    ...(anyNewContent || anyNewHoldingCat ? { new: true } : {}),
                };

                return {
                    ...node,
                    children: [
                        ...actualCategoryChildren,
                        consolidatedContentCategory
                    ]
                };
            }

            // If no consolidation needed, just return with processed children
            return {
                ...node,
                children: processedChildren
            };
        }

        return node;
    };

    // Helper function to merge duplicate categories
    const mergeDuplicateCategories = (node: any): { node: any, merged: boolean } => {
        if (!node || node.type !== 'category') {
            return { node, merged: false };
        }

        let merged = false;
        const processedChildren: any[] = [];
        const seenNames = new Set<string>();

        if (Array.isArray(node.children)) {
            for (const child of node.children) {
                if (child.type === 'category') {
                    const processedChild = mergeDuplicateCategories(child);
                    if (processedChild.merged) merged = true;

                    const childNn = normalizeCategoryName(child.name);
                    if (seenNames.has(childNn)) {
                        // Merge with existing category
                        const existingChild = processedChildren.find(
                            (c: any) => c.type === 'category' && normalizeCategoryName(c.name) === childNn
                        );
                        if (existingChild && existingChild.children && child.children) {
                            for (const grandchild of child.children) {
                                if (
                                    grandchild.type === 'content' &&
                                    grandchild.text &&
                                    grandchild.new !== true &&
                                    existingChild.children.some(
                                        (c: any) =>
                                            c.type === 'content' &&
                                            c.new !== true &&
                                            normalizeCategoryName(c.text || '') ===
                                                normalizeCategoryName(grandchild.text)
                                    )
                                ) {
                                    continue;
                                }
                                existingChild.children.push(grandchild);
                            }
                            if (!existingChild.id && processedChild.node.id) {
                                existingChild.id = processedChild.node.id;
                            }
                            merged = true;
                        }
                    } else {
                        seenNames.add(childNn);
                        processedChildren.push(processedChild.node);
                    }
                } else {
                    processedChildren.push(child);
                }
            }
        }

        return {
            node: { ...node, children: processedChildren },
            merged
        };
    };

    // Helper function to create or find the "Content of [Document Name]" category
    const getOrCreateContentCategory = async (parentId: string, documentName: string): Promise<string> => {
        // Always create the content category under the root (parentId)
        // Include the new document name if we're creating a new document
        let categoryName: string;
        if (createNewDocument && newDocumentName.trim()) {
            categoryName = `Content of ${newDocumentName.trim()}`;
        } else {
            categoryName = `Content of ${documentName}`;
        }
        
        // First try to find existing category under the root
        const existingCategory = await findExistingCategory(parentId, categoryName);
        if (existingCategory) {
            return existingCategory;
        }
        
        // Create new category if it doesn't exist
        const newCategoryResponse = await api.post('/categories', {
            name: categoryName,
            parentId: parentId,
            description: "Content items from structure expansion"
        });
        
        return newCategoryResponse.data.id;
    };

    // Helper function to create tree structure
    // Process the expansion tree by adding content to existing categories and creating new ones
    const processExpansionTree = async (node: any, parentId: string, isRoot: boolean = false, mode: 'with-content' | 'content-in-category' | 'categories-only' = 'with-content', rootCategoryId: string) => {
        // Skip processing the root category - it already exists
        if (isRoot && node.type === 'category') {
            if (Array.isArray(node.children)) {
                for (const child of node.children) {
                    await processExpansionTree(child, parentId, false, mode, rootCategoryId); // Pass rootId as last parameter
                }
            }
            return parentId;
        }

        // Additional check: if this looks like a root category duplicate, skip it
        if (
            node.type === 'category' &&
            !node.new &&
            existingCategories[0]?.name != null &&
            normalizeCategoryName(node.name) === normalizeCategoryName(existingCategories[0].name)
        ) {
            if (Array.isArray(node.children)) {
                for (const child of node.children) {
                    await processExpansionTree(child, parentId, false, mode, rootCategoryId); // Pass rootId as last parameter
                }
            }
            return parentId;
        }

        if (node.type === 'category') {
            return await processExpansionCategory(node, parentId, mode, rootCategoryId); // Pass rootId as last parameter
        } else if (node.type === 'content') {
            return await processExpansionContent(node, parentId, mode, rootCategoryId); // Pass rootId as last parameter
        }
        return null;
    };

    // Helper function to collect all content nodes from the tree
    const collectAllContentNodes = (node: any): any[] => {
        let contentNodes: any[] = [];
        
        if (node.type === 'content') {
            contentNodes.push(node);
        }
        
        if (Array.isArray(node.children)) {
            for (const child of node.children) {
                contentNodes = contentNodes.concat(collectAllContentNodes(child));
            }
        }
        
        return contentNodes;
    };

    const findCategoryInExisting = (
        categoryId: string,
        categories: Category[] = categorySnapshotRef.current.length > 0
            ? categorySnapshotRef.current
            : existingCategories
    ): Category | null => {
        for (const category of categories) {
            if (category.id === categoryId) {
                return category;
            }
            if (category.children?.length) {
                const found = findCategoryInExisting(categoryId, category.children);
                if (found) return found;
            }
        }
        return null;
    };

    const isExpansionContentToApply = (node: any): boolean =>
        node?.type === 'content' && node.new === true;

    const isPreviewOnlyCategory = (node: any): boolean =>
        node?.type === 'category' &&
        !node.new &&
        normalizeCategoryName(node.name) === normalizeCategoryName(PREVIEW_UNCATEGORIZED_CATEGORY);

    /** Preview tree: paths to new categories/content only (less noisy than full document). */
    const buildExpandPreviewTree = useCallback((node: any, underNewCategory = false): any | null => {
        if (!node) return null;
        if (node.type === 'content') {
            // Content under a new category is always part of the delta, even if the row
            // was not explicitly flagged (some clustering paths omit new on leaves).
            return node.new === true || underNewCategory ? node : null;
        }
        if (node.type === 'category' && node.name) {
            if (isPreviewOnlyCategory(node)) {
                return null;
            }
            const isNew = node.new === true;
            const children = (node.children || [])
                .map((child: any) => buildExpandPreviewTree(child, underNewCategory || isNew))
                .filter(Boolean);
            // New folder, or existing folder that received new placements / subfolders.
            if (isNew || children.length > 0) {
                return { ...node, children };
            }
            return null;
        }
        return null;
    }, []);

    /**
     * createCategory() flips contentCategory to false while childrenIds still hold content indices,
     * which hides existing rows in the UI. Move them into "Content of <name>" before adding subcategories.
     */
    const ensureParentCanAcceptSubcategories = async (parentId: string): Promise<void> => {
        if (contentBucketByParentIdRef.current.has(parentId)) {
            return;
        }

        const catResponse = await api.get(`/categories?categoryId=${encodeURIComponent(parentId)}`);
        const parent = catResponse.data;
        if (!categoryIsContentCategory(parent)) {
            return;
        }

        const contentsResponse = await api.get(`/content/list?categoryId=${encodeURIComponent(parentId)}`);
        const contents = contentsResponse.data ?? [];
        if (contents.length === 0) {
            return;
        }

        const bucketName = `Content of ${parent.name}`;
        let bucketId: string | null = findExistingCategory(parentId, bucketName);
        if (!bucketId) {
            const response = await api.post('/categories', {
                name: bucketName,
                parentId,
                description: parent.description || '',
            });
            bucketId = response.data.id ?? null;
        }
        if (!bucketId) {
            console.error('Expand apply: could not create content bucket for', parentId);
            return;
        }
        contentBucketByParentIdRef.current.set(parentId, bucketId);

        for (const content of contents) {
            await api.put('/content/move', null, {
                params: { contentId: content.id, newParentId: bucketId },
            });
        }
    };

    const resolveContentParentId = (parentId: string): string =>
        contentBucketByParentIdRef.current.get(parentId) ?? parentId;

    // Helper function to process all content nodes into the content category
    const processAllContentIntoCategory = async (contentNodes: any[], contentCategoryId: string) => {
        for (const contentNode of contentNodes) {
            if (!isExpansionContentToApply(contentNode)) {
                continue;
            }
            if (contentNode.text && contentNode.type === 'content') {
                await postContentIfAbsent(
                    contentCategoryId,
                    subcaseName,
                    contentNode.text,
                );
            } else if (contentNode.index !== undefined && expansionResult?.originalContents) {
                const originalContent = expansionResult.originalContents[contentNode.index];
                if (originalContent) {
                    await postContentIfAbsent(
                        contentCategoryId,
                        originalContent.subcase || subcaseName,
                        originalContent.text,
                        originalContent.richTextHtml,
                        originalContent.richTextDelta,
                    );
                }
            }
        }
    };

    const subcaseForExpansionContent = (contentNode: any): string => {
        if (contentNode?.new === true) {
            return subcaseName;
        }
        const existing = contentNode?.subcase;
        if (typeof existing === 'string' && existing.trim()) {
            return existing.trim();
        }
        return subcaseName;
    };

    /** Create a content row in a new document (never uses ids from the source document). */
    const createExpansionContentInCategory = async (parentId: string, contentNode: any) => {
        if (contentNode.text && contentNode.type === 'content') {
            if (createNewDocument) {
                await api.post('/content', {
                    text: contentNode.text,
                    subcase: subcaseForExpansionContent(contentNode),
                    parentId,
                    skipDuplicateCheck: true,
                });
            } else {
                await postContentIfAbsent(
                    parentId,
                    subcaseForExpansionContent(contentNode),
                    contentNode.text,
                );
            }
        } else if (contentNode.index !== undefined && expansionResult?.originalContents) {
            const originalContent = expansionResult.originalContents[contentNode.index];
            if (originalContent) {
                if (createNewDocument) {
                    await api.post('/content', {
                        text: originalContent.text,
                        subcase: originalContent.subcase || subcaseName,
                        parentId,
                        skipDuplicateCheck: true,
                        ...(originalContent.richTextHtml ? { richTextHtml: originalContent.richTextHtml } : {}),
                        ...(originalContent.richTextDelta ? { richTextDelta: originalContent.richTextDelta } : {}),
                        ...(typeof originalContent.attachmentsJson === 'string' ? { attachmentsJson: originalContent.attachmentsJson } : {}),
                    });
                } else {
                    await postContentIfAbsent(
                        parentId,
                        originalContent.subcase || subcaseName,
                        originalContent.text,
                        originalContent.richTextHtml,
                        originalContent.richTextDelta,
                    );
                }
            }
        }
    };

    /**
     * Build the full expanded tree under a fresh document root (same approach as SuggestedTreeDialog).
     * Ignores category ids from the source document so the original document is not modified.
     */
    const createExpansionTreeInDocument = async (
        parentId: string,
        node: any,
        withContent: boolean
    ): Promise<void> => {
        if (!node || isPreviewOnlyCategory(node)) {
            return;
        }

        if (node.type !== 'category' || !Array.isArray(node.children)) {
            return;
        }

        const categoryChildren = node.children.filter(
            (child: any) => child.type === 'category' && !isPreviewOnlyCategory(child)
        );
        const contentChildren = node.children.filter((child: any) => child.type === 'content');
        const hasCategory = categoryChildren.length > 0;
        const hasContent = contentChildren.length > 0;

        if (hasCategory && hasContent) {
            for (const child of categoryChildren) {
                const categoryRes = await api.post('/categories', {
                    name: child.name,
                    parentId,
                    description: child.description || '',
                });
                await createExpansionTreeInDocument(categoryRes.data.id, child, withContent);
            }
            if (withContent) {
                const bucketRes = await api.post('/categories', {
                    name: PREVIEW_UNCATEGORIZED_CATEGORY,
                    parentId,
                    description: 'Content that could not be placed in specific categories',
                });
                for (const child of contentChildren) {
                    await createExpansionContentInCategory(bucketRes.data.id, child);
                }
            }
            return;
        }

        if (hasCategory) {
            for (const child of categoryChildren) {
                const categoryRes = await api.post('/categories', {
                    name: child.name,
                    parentId,
                    description: child.description || '',
                });
                await createExpansionTreeInDocument(categoryRes.data.id, child, withContent);
            }
            return;
        }

        if (hasContent && withContent) {
            for (const child of contentChildren) {
                await createExpansionContentInCategory(parentId, child);
            }
        }
    };

    // Process category nodes in expansion tree
    const processExpansionCategory = async (node: any, parentId: string, mode: 'with-content' | 'content-in-category' | 'categories-only' = 'with-content', rootCategoryId: string) => {
        if (isPreviewOnlyCategory(node)) {
            return null;
        }

        let categoryId: string | null = null;

        if (node.new) {
            await ensureParentCanAcceptSubcategories(parentId);
            const newCategoryResponse = await api.post('/categories', {
                name: node.name,
                parentId,
                description: node.description || '',
            });
            categoryId = newCategoryResponse.data.id;
        } else if (node.id) {
            categoryId = node.id;
        } else {
            categoryId = findExistingCategory(parentId, node.name);
            if (!categoryId) {
                console.warn(
                    `Expand apply: skipping category "${node.name}" — no id in preview and no match under parent (would orphan content if recreated)`
                );
                return null;
            }
        }

        if (
            categoryId &&
            !node.new &&
            Array.isArray(node.children) &&
            node.children.some((child: any) => child.type === 'category' && child.new === true)
        ) {
            await ensureParentCanAcceptSubcategories(categoryId);
        }

        if (
            !categoryId ||
            (!hasNewDescendants(node) && !hasNewContentToApply(node))
        ) {
            return categoryId;
        }

        const resolvedCategoryId = categoryId;

        if (Array.isArray(node.children)) {
            for (const child of node.children) {
                if (mode === 'content-in-category' && child.type === 'content') {
                    continue;
                }
                if (child.type === 'content') {
                    if (child.new === true) {
                        await processExpansionTree(child, resolvedCategoryId, false, mode, rootCategoryId);
                    }
                    continue;
                }
                if (child.type === 'category' && !child.new && !hasNewDescendants(child)) {
                    continue;
                }
                await processExpansionTree(child, resolvedCategoryId, false, mode, rootCategoryId);
            }
        }

        return categoryId;
    };

    // Process content nodes in expansion tree
    const processExpansionContent = async (node: any, parentId: string, mode: 'with-content' | 'content-in-category' | 'categories-only' = 'with-content', rootCategoryId: string) => {
        // Handle different expansion modes
        if (mode === 'categories-only') {
            // Skip content creation - only create categories
            return;
        }

        if (!isExpansionContentToApply(node)) {
            return;
        }

        let targetParentId = parentId;
        
        // If mode is 'content-in-category', redirect content to the special category under the root
        if (mode === 'content-in-category' && currentDocument) {
            try {
                // Use the rootCategoryId that was passed through the tree processing chain
                targetParentId = await getOrCreateContentCategory(rootCategoryId, currentDocument.name);
            } catch (error) {
                console.error('Error creating content category:', error);
                // Fallback to original parent
                targetParentId = parentId;
            }
        } else if (mode === 'with-content') {
            targetParentId = resolveContentParentId(parentId);
        }

        // Handle content nodes with text directly
        if (node.text && node.type === 'content') {
            await postContentIfAbsent(targetParentId, subcaseName, node.text);
            return;
        }

        if (node.index !== undefined && expansionResult?.originalContents) {
            const originalContent = expansionResult.originalContents[node.index];
            if (originalContent) {
                await postContentIfAbsent(
                    targetParentId,
                    originalContent.subcase || subcaseName,
                    originalContent.text,
                    originalContent.richTextHtml,
                    originalContent.richTextDelta,
                );
            }
        }
    };



    // Helper function to generate category name from content
    const generateCategoryNameFromContent = (contentNodes: any[]): string => {
        if (!contentNodes || contentNodes.length === 0) return 'New Category';

        // Try to extract a meaningful name from the first content item
        const firstContent = contentNodes[0];
        if (firstContent.text) {
            const text = firstContent.text.substring(0, 30);
            return text.length < 30 ? text : text + '...';
        }

        return `Content Category (${contentNodes.length} items)`;
    };

    // Helper function to find existing category
    const findExistingCategory = (parentId: string, categoryName: string): string | null => {
        const snapshot =
            categorySnapshotRef.current.length > 0 ? categorySnapshotRef.current : existingCategories;

        const searchInCategories = (categories: Category[], targetParentId: string, targetName: string): string | null => {
            const targetNn = normalizeCategoryName(targetName);
            for (const category of categories) {
                if (
                    category.parentId === targetParentId &&
                    normalizeCategoryName(category.name) === targetNn
                ) {
                    return category.id;
                }
                if (category.children && category.children.length > 0) {
                    const found = searchInCategories(category.children, targetParentId, targetName);
                    if (found) return found;
                }
            }
            return null;
        };

        return searchInCategories(snapshot, parentId, categoryName);
    };

    // Handle apply expansion
    const handleApplyExpansion = async () => {
        if (!processedTree) return;

        setApplying(true);
        setGlobalLoading?.(true);
        contentBucketByParentIdRef.current.clear();
        try {
            if (createNewDocument) {
                // Create new document with expanded tree
                if (!newDocumentName.trim()) {
                    alert('Please enter a document name');
                    return;
                }
                if (!onNewDocument) {
                    alert('Create document function not available');
                    return;
                }

                // Create the new document — build tree fresh; never reuse source category ids.
                const newDocument = await onNewDocument(newDocumentName.trim(), false);

                if (newDocument.categoryId) {
                    categorySnapshotRef.current = [];
                    setExistingCategories([]);

                    const withContent = expansionMode !== 'categories-only';

                    if (expansionMode === 'content-in-category') {
                        await createExpansionTreeInDocument(
                            newDocument.categoryId,
                            processedTree,
                            false
                        );
                        const allContentNodes = collectAllContentNodes(processedTree);
                        if (allContentNodes.length > 0) {
                            const contentCategoryId = await getOrCreateContentCategory(
                                newDocument.categoryId,
                                newDocument.name
                            );
                            for (const contentNode of allContentNodes) {
                                await createExpansionContentInCategory(contentCategoryId, contentNode);
                            }
                        }
                    } else {
                        await createExpansionTreeInDocument(
                            newDocument.categoryId,
                            processedTree,
                            withContent
                        );
                    }

                    if (refreshDocuments) {
                        await refreshDocuments();
                    }

                    // Await full open so the global spinner stays until the tree is loaded.
                    if (onOpenDocument) {
                        try {
                            if (newDocument.categoryId) {
                                const response = await api.get('/documents/subcases', { params: { documentId: newDocument.categoryId} });
                                newDocument.subcases = response.data;
                            }
                            await onOpenDocument(newDocument);
                        } catch (error) {
                            console.error('Error opening new document:', error);
                            throw error;
                        }
                    }
                } else {
                    console.error('New document missing categoryId:', newDocument);
                }
            } else {
                // Apply expansion to existing document
                if (!currentDocument) return;

                if (currentDocument.categoryId) {
                    const freshRoot = await fetchCategoryWithChildren(currentDocument.categoryId);
                    categorySnapshotRef.current = [freshRoot];
                    setExistingCategories([freshRoot]);
                }

                if (isContentOnly) {
                    const targetCategoryId = await resolveContentOnlyTargetCategoryId();
                    const allContentNodes = collectAllContentNodes(processedTree);
                    if (allContentNodes.length > 0) {
                        await processAllContentIntoCategory(allContentNodes, targetCategoryId);
                    }
                } else if (expansionMode === 'content-in-category') {
                    // First, create all categories without content
                    if (expansionResult?.placement && expansionResult.placement.parentId) {
                        await processExpansionTree(processedTree, expansionResult.placement.parentId, true, 'categories-only', expansionResult.placement.parentId);
                    } else {
                        const rootCategory = existingCategories[0];
                        if (!rootCategory) {
                            throw new Error('No root category found for document');
                        }
                        await processExpansionTree(processedTree, rootCategory.id, true, 'categories-only', rootCategory.id);
                    }
                    
                    // Then collect all content nodes and process them into the content category
                    const allContentNodes = collectAllContentNodes(processedTree);
                    if (allContentNodes.length > 0) {
                        let rootCategoryId: string;
                        if (expansionResult?.placement && expansionResult.placement.parentId) {
                            rootCategoryId = expansionResult.placement.parentId;
                        } else {
                            rootCategoryId = existingCategories[0].id;
                        }
                        const contentCategoryId = contentInCategoryTargetId
                            ? contentInCategoryTargetId
                            : await getOrCreateContentCategory(rootCategoryId, currentDocument.name);
                        await processAllContentIntoCategory(allContentNodes, contentCategoryId);
                    }
                } else {
                    // Normal processing for other modes
                    if (expansionResult?.placement && expansionResult.placement.parentId) {
                        await processExpansionTree(processedTree, expansionResult.placement.parentId, true, expansionMode, expansionResult.placement.parentId);
                    } else {
                        const rootCategory = existingCategories[0];
                        if (!rootCategory) {
                            throw new Error('No root category found for document');
                        }
                        await processExpansionTree(processedTree, rootCategory.id, true, expansionMode, rootCategory.id);
                    }
                }

                // Recover content rows orphaned when a content category was flipped to a branch
                if (currentDocument.categoryId) {
                    try {
                        await api.post(
                            `/categories/repair-orphaned-content?categoryId=${encodeURIComponent(currentDocument.categoryId)}`
                        );
                    } catch (repairError) {
                        console.error('Expand apply: orphaned content repair failed', repairError);
                    }
                }

                // Always reload the live category structure after mutating it. Prefer an
                // awaitable open with skip-cache so tab tree cache cannot restore a stale view
                // (e.g. content-only new subcategory under root missing until reopen).
                if (onOpenDocument && currentDocument) {
                    try {
                        if (currentDocument.categoryId) {
                            await refreshDocumentSubcases(currentDocument.categoryId);
                        }
                        await onOpenDocument(currentDocument, { forceExpandRoot: true });
                    } catch (error) {
                        console.error('Error refreshing document after expansion:', error);
                        if (refreshCurrentDocument) {
                            await refreshCurrentDocument();
                        } else {
                            alert(`Error opening document: ${error instanceof Error ? error.message : String(error)}`);
                        }
                    }
                } else if (refreshCurrentDocument) {
                    await refreshCurrentDocument();
                }
            }

            onClose();
        } catch (error: any) {
            console.error('Error applying expansion:', error);
            alert(`Error applying expansion: ${error.message}`);
        } finally {
            setApplying(false);
            setGlobalLoading?.(false);
        }
    };

    // Handle close
    const handleClose = () => {
        setShowContent(true);
        setShowFullDocumentTree(false);
        setSubcaseName('Expanded');
        setMergingOccurred(false);
        setProcessedTree(null);
        onClose();
    };

    const fullTree = processedTree || expansionResult?.tree || null;
    const deltaPreviewTree = useMemo(() => {
        if (!fullTree) return null;
        if (isContentOnly) return fullTree;
        return buildExpandPreviewTree(fullTree);
    }, [fullTree, buildExpandPreviewTree, isContentOnly]);
    const treeToRender = isContentOnly ? fullTree : (showFullDocumentTree ? fullTree : (deltaPreviewTree || fullTree));

    const resolveContentOnlyTargetCategoryId = async (): Promise<string> => {
        const placement = expansionResult?.placement;
        if (!placement) {
            throw new Error('No placement information for content-only expansion');
        }
        if (placement.newSubcategoryName && placement.parentId) {
            const existingId = findExistingCategory(placement.parentId, placement.newSubcategoryName);
            if (existingId) {
                return existingId;
            }
            const response = await api.post('/categories', {
                name: placement.newSubcategoryName,
                parentId: placement.parentId,
                description: 'Content imported from files',
            });
            const createdId = response.data.id as string;
            if (createdId) {
                await api.put('/categories/moveto', null, { params: { categoryId: createdId, moveTo: 0 } });
            }
            return createdId;
        }
        const targetId = placement.targetCategoryId || placement.parentId;
        if (!targetId) {
            throw new Error('No target content category specified');
        }
        return targetId;
    };



    // Render tree structure with new category distinction
    const renderTreeStructure = (node: any, level: number = 0, expansionResult: any = null, showContent: boolean = false): React.ReactNode => {
        if (!node) return null;

        const currentPath = `${node.name || node.text || ''}-${level}`;
        const padding = isRTL(node) ? { pr: level * 2 } : { pl: level * 2 };

        // Category node (including root)
        if (node.type === 'category' && node.name) {
            if (isPreviewOnlyCategory(node)) {
                return null;
            }
            const childNodes = Array.isArray(node.children) ? node.children : [];
            const contentChildCount = childNodes.filter((c: any) => c?.type === 'content').length;
            const nodesToRender = showContent
                ? childNodes
                : childNodes.filter((c: any) => c?.type === 'category');
            const children = nodesToRender.flatMap((child: any) =>
                renderTreeStructure(child, level + 1, expansionResult, showContent)
            );
            const isContentCat = isContentCategory(node);
            const isNew = isNewCategorySimple(node);

            let iconColor = theme.palette.primary.main;
            let textColor: string = theme.palette.text.primary;

            if (isNew) {
                iconColor = theme.palette.success.main;
                textColor = theme.palette.success.main;
            } else if (isContentCat) {
                iconColor = theme.palette.info.main;
            }

            return (
                <Box key={currentPath} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', ...padding, mb: 0.5 }} dir={isRTL(node) ? 'rtl' : 'ltr'}>
                        {isContentCat ? (
                            <DescriptionIcon sx={{ color: iconColor, mr: isRTL(node) ? 0 : 1, ml: isRTL(node) ? 1 : 0 }} />
                        ) : (
                            <FolderIcon sx={{ color: iconColor, mr: isRTL(node) ? 0 : 1, ml: isRTL(node) ? 1 : 0 }} />
                        )}
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 700,
                                wordBreak: 'break-word',
                                textAlign: isRTL(node) ? 'right' : 'left',
                                flex: 1,
                                color: textColor
                            }}
                            dir="auto"
                        >
                            {node.name}
                            {isNew && (
                                <Box component="span" sx={{
                                    ml: 1,
                                    fontSize: '0.75rem',
                                    color: 'success.main',
                                    fontStyle: 'italic'
                                }}>
                                    (new)
                                </Box>
                            )}
                        </Typography>
                    </Box>
                    {!showContent && contentChildCount > 0 && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', ...padding, mb: 0.5 }}
                        >
                            {contentChildCount} content item{contentChildCount === 1 ? '' : 's'}
                            {childNodes.some((c: any) => c?.new === true) ? ' (includes new)' : ''}
                        </Typography>
                    )}
                    {children}
                </Box>
            );
        } else if (showContent && node.type === 'content' && node.text) {
            // Content node with text property (from hierarchical clustering)
            return (
                <Box key={currentPath} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', ...padding, mb: 0.5 }} dir={isRTL(node) ? 'rtl' : 'ltr'}>
                        <DescriptionIcon sx={{ color: 'warning.main', mr: isRTL(node) ? 0 : 1, ml: isRTL(node) ? 1 : 0, fontSize: 20, mt: 0.5 }} />
                        <Typography variant="body2" sx={{ fontStyle: 'italic', wordBreak: 'break-word', whiteSpace: 'pre-wrap', lineHeight: 1.5, textAlign: isRTL(node) ? 'right' : 'left', flex: 1 }} dir="auto">
                            {node.text}
                        </Typography>
                    </Box>
                </Box>
            );
        }

        // Content node with index property (from content-based clustering)
        if (showContent && node.type === 'content' && node.index !== undefined) {
            // Try to get the original content if available
            let contentText = `Content ${node.index + 1}`;

            if (expansionResult?.originalContents && expansionResult.originalContents[node.index]) {
                const originalContent = expansionResult.originalContents[node.index];
                contentText = originalContent.text || contentText;
            }

            return (
                <Box key={currentPath} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', ...padding, mb: 0.5 }} dir={isRTL(node) ? 'rtl' : 'ltr'}>
                        <Typography variant="body2" sx={{ fontStyle: 'italic', wordBreak: 'break-word', whiteSpace: 'pre-wrap', lineHeight: 1.5, textAlign: isRTL(node) ? 'right' : 'left', flex: 1 }} dir="auto">
                            {contentText}
                        </Typography>
                    </Box>
                </Box>
            );
        }

        return null;
    };

    return (
        <Dialog
            open={open}
            onClose={(_event, reason) => {
                if (reason === 'backdropClick') return;
                handleClose();
            }}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle>
                {isContentOnly ? 'Content preview' : 'Add to document preview'}
                {!isContentOnly && mergingOccurred && (
                    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        Note: Duplicate categories were merged automatically
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ position: 'relative' }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            {isContentOnly ? 'Import configuration' : 'Expansion configuration'}
                        </Typography>
                    {showContent && (
                        <Tooltip title={getTooltipTextForState('suggest-tree-subcase-name', true)} placement="top">
                            <Box sx={{ mt: 2 }}>
                                <TextField
                                    label="Subcase name"
                                    value={subcaseName}
                                    onChange={(e) => setSubcaseName(e.target.value)}
                                    size="small"
                                    fullWidth
                                    placeholder="e.g. Expanded, New Content"
                                />
                            </Box>
                        </Tooltip>
                    )}
                </Box>

                {/* Preview toggles */}
                <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Tooltip title={getTooltipTextForState('suggest-tree-show-content', true)} placement="top">
                        <FormControlLabel
                            control={<Checkbox checked={showContent} onChange={e => setShowContent(e.target.checked)} />}
                            label="Show content text"
                        />
                    </Tooltip>
                    {!isContentOnly && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showFullDocumentTree}
                                    onChange={e => setShowFullDocumentTree(e.target.checked)}
                                />
                            }
                            label="Show full category structure"
                        />
                    )}
                </Box>

                {/* Unified Tree Structure */}
                {treeToRender && (
                    <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                                <Typography variant="h6">
                                    {isContentOnly
                                        ? 'Imported content (preview)'
                                        : showFullDocumentTree
                                        ? 'Complete Document Structure (Including New Content)'
                                        : 'New Categories and Content (Preview)'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {typeof expansionResult?.segmentCount === 'number'
                                        ? `${expansionResult.segmentCount} text segment${expansionResult.segmentCount === 1 ? '' : 's'} · `
                                        : ''}
                                    {(() => {
                                        const created = countContentNodes(isContentOnly ? fullTree : (deltaPreviewTree || fullTree));
                                        return `${created} content${created === 1 ? '' : 's'} created`;
                                    })()}
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{
                                maxHeight: 400,
                                overflow: 'auto',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 2,
                                bgcolor: 'background.default',
                            }}>
                                {renderTreeStructure(treeToRender, 0, expansionResult, showContent)}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* Expansion Options */}
                {!isContentOnly && (
                <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        <Tooltip title={getTooltipTextForState('expand-tree-apply-mode', true)} placement="top">
                            <span>Apply mode</span>
                        </Tooltip>
                    </Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={expansionMode === 'with-content'}
                                onChange={() => setExpansionMode('with-content')}
                            />
                        }
                        label="Content in folders (default)"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={expansionMode === 'content-in-category'}
                                onChange={() => setExpansionMode('content-in-category')}
                            />
                        }
                        label="Keep content separate"
                    />
                    {expansionMode === 'content-in-category' && !createNewDocument && (
                        <Box sx={{ ml: 4, mt: 1, mb: 1, maxWidth: 480 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="content-in-category-target-label">Save content to</InputLabel>
                                <Select
                                    labelId="content-in-category-target-label"
                                    label="Save content to"
                                    value={contentInCategoryTargetId}
                                    onChange={(e) => setContentInCategoryTargetId(e.target.value)}
                                >
                                    <MenuItem value="">
                                        New &quot;Content of {createNewDocument && newDocumentName.trim() ? newDocumentName.trim() : (currentDocument?.name || 'Document')}&quot; category
                                    </MenuItem>
                                    {contentCategoryOptions.map((opt) => (
                                        <MenuItem key={opt.id} value={opt.id}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={expansionMode === 'categories-only'}
                                onChange={() => setExpansionMode('categories-only')}
                            />
                        }
                        label="Structure only"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={createNewDocument}
                                onChange={(e) => handleCreateNewDocumentChange(e.target.checked)}
                            />
                        }
                        label="Create as new document"
                    />
                    {createNewDocument && (
                        <Box sx={{ mt: 1, ml: 4 }}>
                            <TextField
                                label="New document name"
                                value={newDocumentName}
                                onChange={(e) => setNewDocumentName(e.target.value)}
                                placeholder="Enter document name"
                                size="small"
                                fullWidth
                                sx={{ mb: 1 }}
                            />
                        </Box>
                    )}
                </Box>
                )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={applying}>
                    Cancel
                </Button>
                <Button
                    onClick={handleApplyExpansion}
                    variant="contained"
                    disabled={applying || !currentDocument || !currentDocument.id || fetchingCategories}
                    startIcon={<AddIcon />}
                >
                    Apply Expansion
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExpandTreeResultsDialog; 


